// frontend/packages/business-console/src/pages/Quotes/useQuoteDetail.js
//
// Data hook for the Quote detail page. Two modes (mirror of
// useQuotesList):
//
//   - Demo: pulls from demoQuoteDetail.js fixtures keyed by quote id.
//   - Real: parallel-fetches the quote + the customer it references
//     via getQuote() + getCustomer(). The backend's GET /:id returns
//     `{ quote, availableActions }`; the customer comes from
//     /v2/crm/customers/:id.
//
// Returns { quote, customer, availableActions, loading, error,
// refresh, isDemo, business? }.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getQuote } from '../../services/quoteService';
import { getCustomer } from '../../services/crmService';
import { getDemoQuoteDetail } from './demoQuoteDetail';

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

export function useQuoteDetail(id) {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [quote, setQuote] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [availableActions, setAvailableActions] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      if (isDemo) {
        const fixture = getDemoQuoteDetail(id);
        if (!fixture) {
          setError(new Error('Demo quote not found'));
          return;
        }
        setQuote(fixture.quote);
        setCustomer(fixture.customer);
        setAvailableActions(fixture.availableActions);
        setBusiness(fixture.business || null);
      } else {
        const res = await getQuote(id);
        setQuote(res.quote);
        setAvailableActions(res.availableActions);
        // Customer fetched in parallel for the document header. Errors
        // here are non-fatal — we just render "Customer" as a placeholder.
        try {
          // getCustomer returns { customer, activities, person, ... } —
          // unwrap (matches QuoteEditor/InvoiceEditor). Without this the
          // document header reads `.firstName` off the wrapper → undefined →
          // the "BILL TO: Customer" fallback.
          const res2 = await getCustomer(res.quote.customerId);
          setCustomer(res2?.customer || res2 || null);
        } catch {
          setCustomer(null);
        }
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id, isDemo]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    quote,
    customer,
    availableActions,
    business,
    loading,
    error,
    refresh: load,
    isDemo,
  };
}
